<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import LoaderIcon from "@/components/icons/LoaderIcon.vue";
import { fetchChineseCalendarDay } from "@/api/calculators";
import { ChineseCalendarDay } from "@/types/calculators";
import DayParamsCardComponent from "@/views/Calculators/ChineseCalendar/components/DayParamsCardComponent.vue";
import DaySummaryCardComponent from "@/views/Calculators/ChineseCalendar/components/DaySummaryCardComponent.vue";
import { useI18n } from "vue-i18n";
import DayHoursTableComponent from "@/views/Calculators/ChineseCalendar/components/DayHoursTableComponent.vue";
import BackButton from "@/components/buttons/BackButton.vue";
import { useSafeBack } from "@/utils/useSafeBack";
import { ROUTE_NAMES } from "@/constants/routeNames";

const route = useRoute();
const { t } = useI18n();
const { safeBack } = useSafeBack();

const waitApiCall = ref(true);
const calendarDay = ref<ChineseCalendarDay>();
const queryDate = ref<string>();

onMounted(async () => {
  queryDate.value = route.query.date as string;
  calendarDay.value = await fetchChineseCalendarDay(queryDate.value);
  waitApiCall.value = false;
});
</script>

<template>
  <div
    class="h-full xl:h-screen w-full pt-6 xl:pt-[88px] px-4 overflow-y-auto no-scrollbar"
    :class="{ 'flex flex-col justify-center': waitApiCall }"
  >
    <LoaderIcon spinnerColor="brown" class="mx-auto" v-if="waitApiCall" />
    <div v-else class="flex flex-col max-w-[812px] gap-6 py-4 mx-auto">
      <div class="flex">
        <BackButton @clicked="safeBack(ROUTE_NAMES.HOME)">
          <template #back-text>
            {{ t("calculators.chinese-calendar-day.return-to-month") }}
          </template>
        </BackButton>
      </div>
      <DayParamsCardComponent :date="queryDate" :dayParams="calendarDay" />
      <template v-if="calendarDay.new_season == undefined">
        <DaySummaryCardComponent
          :cardTitle="t('calculators.chinese-calendar-day.day-summary.title')"
          :dayParams="calendarDay"
        />
      </template>
      <template v-else>
        <DaySummaryCardComponent
          :cardTitle="
            t('calculators.chinese-calendar-day.day-summary.season-before', {
              season: calendarDay.season.current,
              time: calendarDay.season.change_time,
            })
          "
          :dayParams="calendarDay"
        />
        <DaySummaryCardComponent
          :cardTitle="
            t('calculators.chinese-calendar-day.day-summary.season-after', {
              season: calendarDay.season.new,
              time: calendarDay.season.change_time,
            })
          "
          :dayParams="calendarDay.new_season"
        />
      </template>
      <DayHoursTableComponent :hoursTable="calendarDay.hours_table" />
    </div>
  </div>
</template>
